import React, { useEffect, useState } from "react";

export default function AdminLatestNewsDialog({ open, onClose, onSave, initial = null, isSaving = false }) {
  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    date: "",
    category: "",
    author: "",
    readTime: "",
    active: false,
    image: null,
    existingImage: "",
  });
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    if (initial) {
      setForm({
        title: initial.title || "",
        excerpt: initial.excerpt || "",
        // normalize incoming date to yyyy-mm-dd for <input type="date">
        date: (() => {
          const d = initial.date;
          if (!d) return "";
          // if it's a Date instance
          if (d instanceof Date) {
            return d.toISOString().slice(0, 10);
          }
          // if it's numeric timestamp (seconds or ms)
          const n = Number(d);
          if (!Number.isNaN(n)) {
            const maybe = n > 1e12 ? new Date(n) : new Date(n * 1000);
            if (!Number.isNaN(maybe.getTime())) return maybe.toISOString().slice(0, 10);
          }
          // try parseable string
          const parsed = new Date(d);
          if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
          return String(d);
        })(),
        category: initial.category || "",
        image: null,
        author: initial.author || "",
        existingImage: initial.image || "",
        // ensure readTime is a string for the input; accept numbers or strings
        readTime:
          initial.readTime !== undefined && initial.readTime !== null
            ? String(initial.readTime)
            : "",
        active: !!initial.active,
      });
      setImageError("");
    } else {
      setForm({ title: "", excerpt: "", date: "", category: "", image: null, existingImage: "", author: "", readTime: "", active: false });
      setImageError("");
    }
  }, [initial, open]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setForm((f) => ({ ...f, image: file }));
    setImageError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (!form.image && !initial) {
      setImageError("An image is required. Please choose a file before saving.");
      return;
    }
    // Build FormData to include files when present
    const fd = new FormData();
    fd.append('title', form.title || '');
    fd.append('excerpt', form.excerpt || '');
    let dateVal = '';
    if (form.date instanceof Date) {
      dateVal = form.date.toISOString();
    } else if (typeof form.date === 'string' && form.date.trim()) {
      // If input type=date produced YYYY-MM-DD, convert to ISO at midnight UTC
      const s = form.date.trim();
      const isoCandidate = new Date(s + 'T00:00:00Z');
      if (!Number.isNaN(isoCandidate.getTime())) {
        dateVal = isoCandidate.toISOString();
      } else {
        // fallback to sending raw string
        dateVal = s;
      }
    }
    fd.append('date', dateVal);
    fd.append('category', form.category || '');
    fd.append('author', form.author || '');
    // normalize readTime to integer string
    if (form.readTime !== undefined && form.readTime !== null && String(form.readTime).trim() !== '') {
      const rt = parseInt(String(form.readTime).trim(), 10);
      fd.append('readTime', Number.isNaN(rt) ? '' : String(rt));
    } else {
      fd.append('readTime', '');
    }
    fd.append('active', form.active ? 'true' : 'false');
    if (form.image) {
      fd.append('image', form.image);
    }
    if (!form.image && form.existingImage) {
      fd.append('existingImage', form.existingImage);
    }
    onSave(fd);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 sm:items-center">
      <div className="relative bg-white rounded shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-3xl font-bold leading-none"
          aria-label="Close dialog"
        >
          &times;
        </button>
        <h2 className="text-xl font-semibold mb-4">{initial ? "Edit News Item" : "Add News Item"}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Title:</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Date:</label>
            <input
              type="date"
              name="date"
              value={form.date || ""}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Author:</label>
            <input
              type="text"
              name="author"
              value={form.author}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Read time (minutes):</label>
            <input
              type="number"
              name="readTime"
              min="0"
              value={form.readTime}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Active</label>
            <input
              type="checkbox"
              name="active"
              checked={form.active}
              onChange={handleChange}
              className="mr-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Category:</label>
            <input
              type="text"
              name="category"
              value={form.category}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Image file{initial ? "" : " (required)"}:</label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full"
              required={!initial}
            />
            {imageError ? <p className="mt-1 text-sm text-red-600">{imageError}</p> : null}
            {!form.image && form.existingImage ? (
              <p className="mt-2 text-xs text-gray-600">Current image will be kept unless you upload a new one.</p>
            ) : null}
          </div>
          <label className="block text-sm font-medium mb-2">Excerpt:</label>
          <textarea name="excerpt" value={form.excerpt} onChange={handleChange} placeholder="Excerpt" className="p-2 border w-full" />

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded" disabled={isSaving}>Cancel</button>
            <button type="submit" className={`px-4 py-2 rounded text-white ${isSaving ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
